import type { DmpSubscriptionV2 } from '@/api/dmpV2.api';
import type { IdentityState } from '@/api/dmpIdentity.api';

export const qualificationKind = (code: string) =>
  (({ sme_firm: 'company', law_firm: 'company', barrister: 'barrister' } as const)[
    code as 'sme_firm' | 'law_firm' | 'barrister'
  ]);

export const onboardingPath = (code: string) =>
  '/profile/identity?product=' + encodeURIComponent(code) + '&onboarding=1';

export function paidQualificationGuidance(subscription: DmpSubscriptionV2, identity: IdentityState, orderId?: number) {
  if (!subscription.is_main_account) return null;
  const candidates = [];
  for (const purchase of subscription.pending_purchases || []) {
    if (orderId !== undefined && purchase.order_id !== orderId) continue;
    const kind = qualificationKind(purchase.product_code);
    if (!kind || identity.qualifications[kind]?.active || purchase.status === 'scheduled') continue;
    if (purchase.status === 'manual' && purchase.reason !== 'qualification_review_required') continue;
    const latest = identity.requests.filter((r) => r.kind === kind).sort((a, b) => b.id - a.id)[0];
    candidates.push({ purchase, needsInformation: latest?.status !== 'pending', requestId: latest?.id || 0 });
  }
  return candidates.find((candidate) => candidate.needsInformation) || candidates[0] || null;
}

// Only opaque record IDs are retained for this tab's reminder; never profile or authentication data.
const seen = new Set<string>();
export function markGuidanceSeen(owner: string, purchase: number, request: number) {
  const key = `dmp-qualification-guidance:${owner}:${purchase}:${request}`;
  seen.add(key);
  try {
    sessionStorage.setItem(key, '1');
  } catch {
    /* In-memory fallback when storage is unavailable. */
  }
}
export function hasSeenGuidance(owner: string, purchase: number, request: number) {
  const key = `dmp-qualification-guidance:${owner}:${purchase}:${request}`;
  try {
    return seen.has(key) || sessionStorage.getItem(key) === '1';
  } catch {
    return seen.has(key);
  }
}

export const isQualificationOnboarding = (pathname: string, search: string) =>
  pathname === '/profile/identity' && new URLSearchParams(search).get('onboarding') === '1';
